<script setup>

import {usePortalContentAndDesignStore} from "../../../../stores/partners/portalContentAndDesignStore";
import {computed} from "vue";

const portalContentAndDesignStore = usePortalContentAndDesignStore();

const handleLoginBackgroundDelete = () => {
    portalContentAndDesignStore.srcRoutes.portal_login_file_background_path = null;
};

const showSidebarOptions = computed(() => {
    return portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_side !== 'none';
});

const sidebarType = computed(() => {
    return portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_type || 'image';
});

</script>

<template>
    <radio-group-inline
        label="Form width"
        name="portal_login_sidebar_width"
        :model-value="portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_width"
        @update:model-value="portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_width = $event"
        :options="[
            { value: 'lg:w-5/12', label: 'Narrow'},
            { value: 'lg:w-6/12', label: 'Regular'},
            { value: 'lg:w-8/12', label: 'Wide'}
        ]"
    >
    </radio-group-inline>

    <!-- Sidebar position and type in one row -->
    <div class="grid grid-cols-2 gap-3">
        <radio-group-inline
            :class="{ 'col-span-2': !showSidebarOptions }"
            label="Sidebar"
            name="portal_login_sidebar_side"
            :model-value="portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_side"
            @update:model-value="portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_side = $event"
            :options="[
                { value: 'left', label: '<svg xmlns=&quot;http://www.w3.org/2000/svg&quot; viewBox=&quot;0 0 24 24&quot; fill=&quot;currentColor&quot;><path d=&quot;M21 3C21.5523 3 22 3.44772 22 4V20C22 20.5523 21.5523 21 21 21H3C2.44772 21 2 20.5523 2 20V4C2 3.44772 2.44772 3 3 3H21ZM15 5H4V19H15V5ZM20 5H17V19H20V5Z&quot;></path></svg>', label_is_html: true, title: 'Left' },
                { value: 'right', label: '<svg xmlns=&quot;http://www.w3.org/2000/svg&quot; viewBox=&quot;0 0 24 24&quot; fill=&quot;currentColor&quot;><path d=&quot;M21 3C21.5523 3 22 3.44772 22 4V20C22 20.5523 21.5523 21 21 21H3C2.44772 21 2 20.5523 2 20V4C2 3.44772 2.44772 3 3 3H21ZM7 5H4V19H7V5ZM20 5H9V19H20V5Z&quot;></path></svg>', label_is_html: true, title: 'Right' },
                { value: 'none', label: '<svg xmlns=&quot;http://www.w3.org/2000/svg&quot; viewBox=&quot;0 0 24 24&quot; fill=&quot;currentColor&quot;><path d=&quot;M21 3C21.5523 3 22 3.44772 22 4V20C22 20.5523 21.5523 21 21 21H3C2.44772 21 2 20.5523 2 20V4C2 3.44772 2.44772 3 3 3H21ZM20 5H4V19H20V5Z&quot;></path></svg>', label_is_html: true, title: 'None' }
            ]"
        >
        </radio-group-inline>
        <radio-group-inline
            v-if="showSidebarOptions"
            label="Sidebar type"
            name="portal_login_sidebar_type"
            :model-value="portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_type || 'image'"
            @update:model-value="portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_type = $event"
            :options="[
                { value: 'image', label: 'Image'},
                { value: 'content', label: 'Content'}
            ]"
        >
        </radio-group-inline>
    </div>

    <template v-if="showSidebarOptions">

        <file-upload
            v-if="sidebarType === 'image'"
            :src="portalContentAndDesignStore.srcRoutes.portal_login_file_background"
            @update:src="portalContentAndDesignStore.srcRoutes.portal_login_file_background = $event"
            :path="portalContentAndDesignStore.srcRoutes.portal_login_file_background_path"
            accept="image/*"
            title="Sidebar image"
            name="portal_login_file_background"
            :upload_url="portalContentAndDesignStore.getFileUploadRoute"
            :delete_url="portalContentAndDesignStore.getFileDeleteRoute"
            @delete:done="handleLoginBackgroundDelete">
        </file-upload>

        <template v-if="sidebarType === 'content'">
            <radio-group-inline-color
                label="Background color"
                name="portal_login_sidebar_bg_color"
                :model-value="portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_bg_color || 'brand'"
                @picked="portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_bg_color = $event.finalValue"
                :options="[
                    { value: 'brand', label: 'Brand', label_is_text: true },
                    { value: 'black', label: 'bg-black', label_is_class: true },
                    { value: 'white', label: 'bg-white', label_is_class: true },
                    { value: 'teal-700', label: 'bg-teal-700', label_is_class: true },
                    { value: 'blue-700', label: 'bg-blue-700', label_is_class: true },
                    { value: 'indigo-700', label: 'bg-indigo-700', label_is_class: true },
                    { value: 'custom', label: 'Custom', custom: { name: 'portal_login_sidebar_bg_color', placeholder: '', type: 'input' }},
                ]"
            />

            <radio-group-inline-color
                label="Text color"
                name="portal_login_sidebar_text_color"
                :model-value="portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_text_color || 'white'"
                @picked="portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_text_color = $event.finalValue"
                :options="[
                    { value: 'white', label: 'bg-white', label_is_class: true },
                    { value: 'black', label: 'bg-black', label_is_class: true },
                    { value: 'teal-700', label: 'bg-teal-700', label_is_class: true },
                    { value: 'blue-700', label: 'bg-blue-700', label_is_class: true },
                    { value: 'indigo-700', label: 'bg-indigo-700', label_is_class: true },
                    { value: 'custom', label: 'Custom', custom: { name: 'portal_login_sidebar_text_color', placeholder: '', type: 'input' }},
                ]"
            />

            <radio-group-inline
                label="Branding"
                name="portal_login_sidebar_branding"
                :model-value="portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_branding || 'none'"
                @update:model-value="portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_branding = $event"
                :options="[
                    { value: 'none', label: 'None'},
                    { value: 'symbol', label: 'Symbol'},
                    { value: 'logo', label: 'Logo'}
                ]"
            >
            </radio-group-inline>

            <text-field
                label="Headline"
                name="portal_login_sidebar_headline"
                :model-value="portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_headline || ''"
                @update:model-value="portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_headline = $event"
                placeholder="e.g. Welcome to Our Partner Program"
            />

            <div>
                <text-editor
                    name="portal_login_sidebar_content"
                    :text="portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_content || ''"
                    @input="(newValue) => { portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_content = newValue }"
                />
            </div>
        </template>
    </template>

    <toggle v-if="portalContentAndDesignStore.portalLayoutSettings.portal_public_pages_different_appearance"
            v-model="portalContentAndDesignStore.portalLayoutSettings.portal_public_pages_different_appearance"
            title="Different setup for other pages" name="portal_public_pages_different_appearance"
            :true-value="true"
    >
        <template #description>
            Use different appearance settings for the sign-up page.
        </template>
    </toggle>
</template>

<style scoped>

</style>
